import React, { createContext, useContext, ReactNode, useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";

interface OmnisearchContextType {
  omnisearch: string | null;
  hasUserSetOmnisearch: boolean;
  setOmnisearch: (value: string) => void;
}

const OmnisearchContext = createContext<OmnisearchContextType | undefined>(undefined);

interface OmnisearchProviderProps {
  children: ReactNode;
  initialOmnisearch?: string;
}

/**
 * Legacy omnisearch context used by `useOmnisearchInput` and a few older pages.
 *
 * @deprecated Use `useOmnisearchState` from `hooks/omnisearch-v2` instead.
 * Note: this is a different component from `providers/OmnisearchProvider`.
 */
export const OmnisearchProvider: React.FC<OmnisearchProviderProps> = ({
  children,
  initialOmnisearch,
}) => {
  // Use React Router's useLocation to get the current URL
  // This re-renders when navigate() is called anywhere in the app
  const location = useLocation();

  // Parse omnisearch from URL query params
  const urlOmnisearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("omnisearch");
  }, [location.search]);

  // Keep internal state for when setOmnisearch is called directly
  // This supports backward compatibility with existing code
  const [internalOmnisearch, setInternalOmnisearch] = useState<string | null>(
    initialOmnisearch ?? null
  );
  const [hasUserSetOmnisearch, setHasUserSetOmnisearch] = useState(false);

  // Track if URL ever had omnisearch param (to distinguish "never set" from "was cleared")
  // This ref is updated synchronously during render (idempotent - only ever sets true)
  const everHadUrlParam = React.useRef(urlOmnisearch !== null);
  if (urlOmnisearch !== null) {
    everHadUrlParam.current = true;
  }

  // Compute omnisearch SYNCHRONOUSLY during render to avoid double-render issues
  // This is the key fix: don't rely on an async effect to derive the value
  const omnisearch =
    urlOmnisearch !== null
      ? urlOmnisearch // URL has value - use it
      : everHadUrlParam.current
        ? "" // URL param was cleared - return empty (not stale internal state)
        : internalOmnisearch; // URL never had param - use internal state

  // Keep internal state in sync for setOmnisearch callers
  // IMPORTANT: Only update if value actually changes to avoid spurious re-renders
  useEffect(() => {
    if (urlOmnisearch !== null) {
      if (internalOmnisearch !== urlOmnisearch) {
        setInternalOmnisearch(urlOmnisearch);
      }
    } else if (everHadUrlParam.current) {
      if (internalOmnisearch !== "" && internalOmnisearch !== null) {
        setInternalOmnisearch("");
      }
    }
  }, [urlOmnisearch, internalOmnisearch]);

  const setOmnisearch = React.useCallback((value: string) => {
    setInternalOmnisearch(value);
    setHasUserSetOmnisearch(true);
  }, []);

  const contextValue = React.useMemo(
    () => ({ omnisearch, setOmnisearch, hasUserSetOmnisearch }),
    [omnisearch, setOmnisearch, hasUserSetOmnisearch]
  );

  return <OmnisearchContext.Provider value={contextValue}>{children}</OmnisearchContext.Provider>;
};

/** @deprecated Use `useOmnisearchState` from `hooks/omnisearch-v2` instead. */
export const useOmnisearchContext = (): OmnisearchContextType | undefined => {
  return useContext(OmnisearchContext);
};
